import { roosterUploadAan } from '../features'
import { bepaalStatus, bezettingPercentage, urenPerSoort } from '../inzet'
import type { Me } from '../me-types'
import type {
  Inzetregel,
  MedewerkerDetail,
  MedewerkerInzet,
  TeamOverzicht,
} from '../medewerker-types'
import type { Semester } from '../semester-types'

export interface RuweMedewerker {
  id: string
  naam: string
  email: string
  aanstellingFte: number | null
  beschikbaar: number | null
  regels: Inzetregel[]
}

function regel(
  soort: Inzetregel['soort'],
  omschrijving: string,
  uren: number,
  extra: Partial<Omit<Inzetregel, 'soort' | 'omschrijving' | 'uren'>> = {},
): Inzetregel {
  return {
    soort,
    omschrijving,
    uren,
    groepCode: extra.groepCode ?? null,
    vak: extra.vak ?? null,
    weken: extra.weken ?? null,
    toelichting: extra.toelichting ?? null,
  }
}

const OVERLEG = 'Teamoverleg en studiedagen'

export const MEDEWERKERS: RuweMedewerker[] = [
  {
    id: 'ewout',
    naam: 'Ewout',
    email: 'ewout@windesheim.nl',
    aanstellingFte: 0.8,
    beschikbaar: 600,
    regels: [
      regel('les', 'Lezen', 96, { groepCode: 'NT2-B2a', vak: 'Lezen', weken: 24 }),
      regel('les', 'Examentraining lezen', 84, { groepCode: 'NT2-B2a', vak: 'Examentraining', weken: 21 }),
      regel('les', 'Grammatica', 72, { groepCode: 'NT2-B1a', vak: 'Grammatica', weken: 24 }),
      regel('mentoraat', 'Mentoraat NT2-B2a', 48, { groepCode: 'NT2-B2a', weken: 24 }),
      regel('overig', OVERLEG, 60),
      regel('overig', 'Ontwikkeltijd toetsbank', 80, { toelichting: 'Herziening leesitems B2' }),
    ],
  },
  {
    id: 'geert',
    naam: 'Geert',
    email: 'geert@windesheim.nl',
    aanstellingFte: 0.7,
    beschikbaar: 520,
    regels: [
      regel('les', 'Spreken en gesprekken', 120, { groepCode: 'NT2-A2a', vak: 'Spreken', weken: 24 }),
      regel('les', 'Spreken en gesprekken', 96, { groepCode: 'NT2-A2b', vak: 'Spreken', weken: 24 }),
      regel('les', 'Taalportfolio', 48, { groepCode: 'NT2-A2a', vak: 'Taalportfolio', weken: 16 }),
      regel('mentoraat', 'Mentoraat NT2-A2a', 48, { groepCode: 'NT2-A2a', weken: 24 }),
      regel('coaching', 'Individuele taalcoaching', 72, { toelichting: 'Zes deelnemers' }),
      regel('overig', OVERLEG, 60),
      regel('overig', 'Intakegesprekken instroom', 66),
    ],
  },
  {
    id: 'gul',
    naam: 'Gul',
    email: 'gul@windesheim.nl',
    aanstellingFte: 0.65,
    beschikbaar: 480,
    regels: [
      regel('les', 'Lezen', 96, { groepCode: 'NT2-A2b', vak: 'Lezen', weken: 24 }),
      regel('les', 'Schrijven', 96, { groepCode: 'NT2-A2b', vak: 'Schrijven', weken: 24 }),
      regel('les', 'Alfabetisering', 72, { groepCode: 'NT2-A1', vak: 'Alfabetisering', weken: 18 }),
      regel('mentoraat', 'Mentoraat NT2-A2b', 48, { groepCode: 'NT2-A2b', weken: 24 }),
      regel('coaching', 'Begeleiding alfabetisering', 60),
      regel('overig', OVERLEG, 60),
      regel('overig', 'Open dagen en voorlichting', 58),
    ],
  },
  {
    id: 'henrike',
    naam: 'Henrike',
    email: 'henrike@windesheim.nl',
    aanstellingFte: 0.5,
    beschikbaar: 400,
    regels: [
      regel('les', 'Schrijven', 96, { groepCode: 'NT2-B1a', vak: 'Schrijven', weken: 24 }),
      regel('les', 'Schrijven', 96, { groepCode: 'NT2-B1b', vak: 'Schrijven', weken: 24 }),
      regel('les', 'Taalportfolio', 40, { groepCode: 'NT2-B1a', vak: 'Taalportfolio', weken: 10 }),
      regel('mentoraat', 'Mentoraat NT2-B1b', 48, { groepCode: 'NT2-B1b', weken: 24 }),
      regel('overig', OVERLEG, 60),
      regel('overig', 'Ontwikkeltijd schrijfvaardigheid', 56),
    ],
  },
  {
    id: 'jessica',
    naam: 'Jessica',
    email: 'jessica@windesheim.nl',
    aanstellingFte: 0.75,
    beschikbaar: 560,
    regels: [
      regel('les', 'Luisteren', 96, { groepCode: 'NT2-B1a', vak: 'Luisteren', weken: 24 }),
      regel('les', 'Luisteren', 96, { groepCode: 'NT2-B2b', vak: 'Luisteren', weken: 24 }),
      regel('les', 'Examentraining luisteren', 84, { groepCode: 'NT2-B2b', vak: 'Examentraining', weken: 21 }),
      regel('mentoraat', 'Mentoraat NT2-B1a', 48, { groepCode: 'NT2-B1a', weken: 24 }),
      regel('coaching', 'Taalcoaching doorstroom hbo', 60),
      regel('overig', OVERLEG, 60),
      regel('overig', 'Roosterwerk NT2', 44),
    ],
  },
  {
    id: 'lonneke',
    naam: 'Lonneke',
    email: 'lonneke@windesheim.nl',
    aanstellingFte: 0.45,
    beschikbaar: 360,
    regels: [
      regel('les', 'Lezen', 72, { groepCode: 'NT2-A1', vak: 'Lezen', weken: 18 }),
      regel('les', 'Spreken', 72, { groepCode: 'NT2-A1', vak: 'Spreken', weken: 18 }),
      regel('mentoraat', 'Mentoraat NT2-A1', 48, { groepCode: 'NT2-A1', weken: 18 }),
      regel('overig', OVERLEG, 60),
      regel('overig', 'Materiaalbeheer taallab', 48),
    ],
  },
  {
    id: 'mariann',
    naam: 'Mariann',
    email: 'mariann@windesheim.nl',
    aanstellingFte: 0.4,
    beschikbaar: 320,
    regels: [
      regel('les', 'Grammatica', 72, { groepCode: 'NT2-A2a', vak: 'Grammatica', weken: 24 }),
      regel('les', 'Grammatica', 72, { groepCode: 'NT2-A2b', vak: 'Grammatica', weken: 24 }),
      regel('coaching', 'Individuele taalcoaching', 60),
      regel('overig', OVERLEG, 60),
      regel('overig', 'Ontwikkeltijd grammaticalijn', 48),
    ],
  },
  {
    id: 'marieke',
    naam: 'Marieke',
    email: 'marieke@windesheim.nl',
    aanstellingFte: 0.8,
    beschikbaar: 600,
    regels: [
      regel('les', 'Lezen', 96, { groepCode: 'NT2-B2b', vak: 'Lezen', weken: 24 }),
      regel('les', 'Schrijven', 96, { groepCode: 'NT2-B2b', vak: 'Schrijven', weken: 24 }),
      regel('les', 'Examentraining schrijven', 84, { groepCode: 'NT2-B2a', vak: 'Examentraining', weken: 21 }),
      regel('les', 'Studievaardigheden', 60, { groepCode: 'NT2-C1', vak: 'Studievaardigheden', weken: 15 }),
      regel('mentoraat', 'Mentoraat NT2-B2b', 48, { groepCode: 'NT2-B2b', weken: 24 }),
      regel('coaching', 'Coaching zij-instroom', 72),
      regel('overig', OVERLEG, 60),
      regel('overig', 'Curriculumcommissie NT2', 78, { toelichting: 'Herijking leerlijn B2' }),
    ],
  },
  {
    id: 'marjan',
    naam: 'Marjan',
    email: 'marjan@windesheim.nl',
    aanstellingFte: 0.6,
    beschikbaar: 480,
    regels: [
      regel('les', 'Luisteren', 96, { groepCode: 'NT2-A2a', vak: 'Luisteren', weken: 24 }),
      regel('les', 'Taalportfolio', 48, { groepCode: 'NT2-A2b', vak: 'Taalportfolio', weken: 16 }),
      regel('les', 'Spreken', 96, { groepCode: 'NT2-B1b', vak: 'Spreken', weken: 24 }),
      regel('mentoraat', 'Mentoraat NT2-C1', 40, { groepCode: 'NT2-C1', weken: 20 }),
      regel('overig', OVERLEG, 60),
      regel('overig', 'Begeleiding stagiairs', 60),
    ],
  },
  {
    id: 'marleen',
    naam: 'Marleen',
    email: 'marleen@windesheim.nl',
    aanstellingFte: 0.3,
    beschikbaar: 240,
    regels: [
      regel('les', 'Schrijven', 72, { groepCode: 'NT2-A1', vak: 'Schrijven', weken: 18 }),
      regel('les', 'Taalportfolio', 36, { groepCode: 'NT2-A1', vak: 'Taalportfolio', weken: 12 }),
      regel('coaching', 'Begeleiding anderstalige ouders', 48),
      regel('overig', OVERLEG, 40),
      regel('overig', 'Ontwikkeltijd alfa-materiaal', 40),
    ],
  },
  {
    id: 'saben',
    naam: 'Saben',
    email: 'saben@windesheim.nl',
    aanstellingFte: 0.55,
    beschikbaar: 440,
    regels: [
      regel('les', 'Spreken', 96, { groepCode: 'NT2-B2a', vak: 'Spreken', weken: 24 }),
      regel('les', 'Spreken', 96, { groepCode: 'NT2-B2b', vak: 'Spreken', weken: 24 }),
      regel('les', 'Examentraining spreken', 84, { groepCode: 'NT2-B2b', vak: 'Examentraining', weken: 21 }),
      regel('coaching', 'Individuele examenbegeleiding', 60),
      regel('overig', OVERLEG, 60),
      regel('overig', 'Toetscommissie', 36),
    ],
  },
  {
    id: 'sevan',
    naam: 'Sevan',
    email: 'sevan@windesheim.nl',
    aanstellingFte: 0.5,
    beschikbaar: 400,
    regels: [
      regel('les', 'Lezen', 96, { groepCode: 'NT2-B1b', vak: 'Lezen', weken: 24 }),
      regel('les', 'Grammatica', 72, { groepCode: 'NT2-B1b', vak: 'Grammatica', weken: 24 }),
      regel('les', 'Grammatica', 72, { groepCode: 'NT2-B2a', vak: 'Grammatica', weken: 24 }),
      regel('coaching', 'Taalcoaching mbo-doorstroom', 48),
      regel('overig', OVERLEG, 60),
      regel('overig', 'Digitale leeromgeving', 44),
    ],
  },
  {
    id: 'vincent',
    naam: 'Vincent',
    email: 'vincent@windesheim.nl',
    aanstellingFte: 0.7,
    beschikbaar: 520,
    regels: [
      regel('les', 'Lezen', 72, { groepCode: 'NT2-C1', vak: 'Lezen', weken: 18 }),
      regel('les', 'Schrijven', 72, { groepCode: 'NT2-C1', vak: 'Schrijven', weken: 18 }),
      regel('les', 'Luisteren', 48, { groepCode: 'NT2-C1', vak: 'Luisteren', weken: 12 }),
      regel('les', 'Examentraining lezen', 84, { groepCode: 'NT2-B2b', vak: 'Examentraining', weken: 21 }),
      regel('mentoraat', 'Mentoraat NT2-A2b', 48, { groepCode: 'NT2-A2b', weken: 24 }),
      regel('overig', OVERLEG, 60),
      regel('overig', 'Ontwikkeltijd C1-lijn', 60),
    ],
  },
  {
    id: 'wilma',
    naam: 'Wilma',
    email: 'wilma@windesheim.nl',
    aanstellingFte: null,
    beschikbaar: null,
    regels: [
      regel('les', 'Luisteren', 72, { groepCode: 'NT2-A2b', vak: 'Luisteren', weken: 24 }),
      regel('les', 'Taalportfolio', 48, { groepCode: 'NT2-B2a', vak: 'Taalportfolio', weken: 16 }),
      regel('coaching', 'Individuele taalcoaching', 48),
      regel('overig', OVERLEG, 60),
      regel('overig', 'Werkveldcontacten en stages', 40, { toelichting: 'Aanstelling nog niet vastgelegd' }),
    ],
  },
]

const OPENSTAAND: Inzetregel[] = [
  regel('les', 'Luisteren', 48, {
    groepCode: 'NT2-A1',
    vak: 'Luisteren',
    weken: 12,
    toelichting: 'Nog geen docent gekoppeld',
  }),
  regel('les', 'Examentraining schrijven', 60, {
    groepCode: 'NT2-B1b',
    vak: 'Examentraining',
    weken: 20,
  }),
  regel('coaching', 'Intakes en niveaubepaling nieuwe instroom', 64, {
    toelichting: 'Verdeling volgt na de instroomtelling',
  }),
  regel('overig', 'Voorbereiding open avond september', 32),
]

export const GROEP_META: Record<
  string,
  { naam: string; niveau: string; aantalStudenten: number; startDatum: string }
> = {
  'NT2-A1': { naam: 'Alfa en A1 instroom', niveau: 'A1', aantalStudenten: 14, startDatum: '2026-02-02' },
  'NT2-A2a': { naam: 'A2 dagtraject', niveau: 'A2', aantalStudenten: 18, startDatum: '2026-02-02' },
  'NT2-A2b': { naam: 'A2 avondtraject', niveau: 'A2', aantalStudenten: 15, startDatum: '2026-02-09' },
  'NT2-B1a': { naam: 'B1 dagtraject', niveau: 'B1', aantalStudenten: 20, startDatum: '2026-02-02' },
  'NT2-B1b': { naam: 'B1 avondtraject', niveau: 'B1', aantalStudenten: 17, startDatum: '2026-02-09' },
  'NT2-B2a': { naam: 'B2 staatsexamen I', niveau: 'B2', aantalStudenten: 22, startDatum: '2026-02-02' },
  'NT2-B2b': { naam: 'B2 staatsexamen II', niveau: 'B2', aantalStudenten: 19, startDatum: '2026-02-02' },
  'NT2-C1': { naam: 'C1 doorstroom hbo', niveau: 'C1', aantalStudenten: 12, startDatum: '2026-02-16' },
}

export const SEMESTERS: Semester[] = [
  {
    id: 's1-2526',
    naam: 'Semester 1 2025-2026',
    jaar: 2025,
    volgnummer: 1,
    startDatum: '2025-08-25',
    eindDatum: '2026-01-30',
    status: 'afgerond',
  },
  {
    id: 's2-2526',
    naam: 'Semester 2 2025-2026',
    jaar: 2025,
    volgnummer: 2,
    startDatum: '2026-02-02',
    eindDatum: '2026-07-10',
    status: 'actief',
  },
  {
    id: 's1-2627',
    naam: 'Semester 1 2026-2027',
    jaar: 2026,
    volgnummer: 1,
    startDatum: '2026-08-24',
    eindDatum: '2027-01-29',
    status: 'concept',
  },
]

export const HUIDIG_SEMESTER = 's2-2526'

export const ME: Me = {
  user: {
    id: 'teamleider-1',
    naam: 'Wilma Kroon',
    email: 'w.kroon@windesheim.nl',
    rol: 'teamleider',
  },
  product: roosterUploadAan ? 'upload' : 'beheer',
}

export interface RuweTaak extends Inzetregel {
  id: string
  medewerkerId: string | null
}

function naarInzet(bron: RuweMedewerker, regels: Inzetregel[]): MedewerkerInzet {
  const totaal = regels.reduce((som, item) => som + item.uren, 0)
  const beschikbaar = bron.beschikbaar
  return {
    medewerkerId: bron.id,
    naam: bron.naam,
    lesUren: urenPerSoort(regels, 'les'),
    mentoraatUren: urenPerSoort(regels, 'mentoraat'),
    coachingUren: urenPerSoort(regels, 'coaching'),
    overigeUren: urenPerSoort(regels, 'overig'),
    totaal,
    beschikbaar,
    balans: beschikbaar === null ? null : beschikbaar - totaal,
    bezettingPercentage: bezettingPercentage(totaal, beschikbaar),
    status: bepaalStatus(totaal, beschikbaar),
    regels,
  }
}

function isEigenRegel(regel: Inzetregel, semesterId: string, index: number): boolean {
  if (semesterId === 's1-2526') return regel.soort !== 'overig' || regel.omschrijving === OVERLEG
  if (semesterId === 's1-2627') {
    if (index >= 6) return regel.soort === 'overig'
    return regel.soort !== 'coaching'
  }
  return true
}

function openstaandVoorSemester(semesterId: string): Inzetregel[] {
  if (semesterId === 's1-2526') return []
  if (semesterId === 's1-2627') {
    const nogTeVerdelen = MEDEWERKERS.flatMap((bron, index) =>
      bron.regels.filter((item) => !isEigenRegel(item, semesterId, index) && item.soort !== 'overig'),
    )
    return [...OPENSTAAND, ...nogTeVerdelen]
  }
  return OPENSTAAND
}

function ruweTaken(semesterId: string): RuweTaak[] {
  const uitVerdeling = MEDEWERKERS.flatMap((bron, index) =>
    bron.regels
      .filter((item) => isEigenRegel(item, semesterId, index))
      .map((item, positie) => ({
        ...item,
        id: `${semesterId}-${bron.id}-${positie}`,
        medewerkerId: bron.id,
      })),
  )
  const openstaand = openstaandVoorSemester(semesterId).map((item, positie) => ({
    ...item,
    id: `${semesterId}-open-${positie}`,
    medewerkerId: null,
  }))
  return [...uitVerdeling, ...openstaand]
}

function alsInzetregel(taak: RuweTaak): Inzetregel {
  return {
    soort: taak.soort,
    omschrijving: taak.omschrijving,
    uren: taak.uren,
    groepCode: taak.groepCode,
    vak: taak.vak,
    weken: taak.weken,
    toelichting: taak.toelichting,
  }
}

export function teamOverzicht(semesterId: string): TeamOverzicht {
  const alle = ruweTaken(semesterId)
  const medewerkers = MEDEWERKERS.map((bron) =>
    naarInzet(
      bron,
      alle.filter((taak) => taak.medewerkerId === bron.id).map(alsInzetregel),
    ),
  )
  return {
    medewerkers,
    nietToegewezen: alle.filter((taak) => taak.medewerkerId === null).map(alsInzetregel),
    totaalToegewezen: medewerkers.reduce((som, item) => som + item.totaal, 0),
    totaalBeschikbaar: medewerkers.reduce((som, item) => som + (item.beschikbaar ?? 0), 0),
  }
}

export function medewerkerDetail(semesterId: string, medewerkerId: string): MedewerkerDetail | null {
  const bron = MEDEWERKERS.find((item) => item.id === medewerkerId)
  if (!bron) return null
  const inzet = teamOverzicht(semesterId).medewerkers.find(
    (item) => item.medewerkerId === medewerkerId,
  )
  if (!inzet) return null
  return {
    medewerkerId: bron.id,
    naam: bron.naam,
    email: bron.email,
    aanstellingFte: bron.aanstellingFte,
    inzet,
  }
}

export function lesregels(semesterId: string, code: string): RuweTaak[] {
  return ruweTaken(semesterId).filter(
    (taak) => taak.groepCode === code && (taak.soort === 'les' || taak.soort === 'mentoraat'),
  )
}

export function overigeRegels(semesterId: string): RuweTaak[] {
  return ruweTaken(semesterId).filter((taak) => taak.soort === 'overig')
}
